import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    event_date: "",
    event_type: "",
    audience_size: "",
    message: "",
    company: "", // honeypot — hidden from users, filled by bots
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSuccess(true);
      toast.success("Inquiry sent! Gary's team will reach out shortly.");
      setForm({
        name: "",
        email: "",
        organization: "",
        event_date: "",
        event_type: "",
        audience_size: "",
        message: "",
        company: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email Cherisse@GaryZelesky.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="section-num">06 / BOOK GARY</span>
            <div className="divider-red" />
          </div>
          <h2 data-testid="booking-heading" className="h-display text-5xl md:text-6xl lg:text-7xl mb-6 pb-2">
            Let's make your<br />event<br />
            <span className="text-[#EC1985] text-4xl md:text-5xl lg:text-6xl">unforgettable.</span>
          </h2>
          <p className="text-white/75 text-lg leading-relaxed mb-8">
            Tell us about your audience and your goals. Gary's team responds quickly — and every keynote is
            customized to align with your culture, your message, and your moment.
          </p>

          <div className="space-y-4 text-white/85">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-[#EC1985] flex-shrink-0 mt-1" />
              <span>Customized keynotes for corporate, dental, association, healthcare & service teams</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-[#EC1985] flex-shrink-0 mt-1" />
              <span>Virtual, in-person, and hybrid formats — domestic and international</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-[#EC1985] flex-shrink-0 mt-1" />
              <span>Direct response from Gary's booking team within 1 business day</span>
            </div>
          </div>

          <div className="mt-10 border-t border-[#262626] pt-6">
            <div className="text-xs uppercase tracking-[0.25em] text-white/50 font-bold">Prefer email?</div>
            <a
              href="mailto:Cherisse@GaryZelesky.com?subject=Gary-I%20have%20to%20have%20you%20at%20my%20event."
              data-testid="email-direct-link"
              className="under-link font-display text-xl md:text-2xl text-white mt-2 inline-block"
            >
              Cherisse@GaryZelesky.com
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="surface p-8 md:p-10 space-y-5"
          >
            {/* Honeypot — invisible to humans, bots fill it and get rejected */}
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
            >
              <label>
                Company (leave blank)
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={update("company")}
                />
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                  Your Name *
                </label>
                <input
                  data-testid="form-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  className="input-base"
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                  Email *
                </label>
                <input
                  data-testid="form-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="input-base"
                  placeholder="jane@company.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                  Organization
                </label>
                <input
                  data-testid="form-organization"
                  type="text"
                  value={form.organization}
                  onChange={update("organization")}
                  className="input-base"
                  placeholder="Company / Association"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                  Event Date
                </label>
                <input
                  data-testid="form-event-date"
                  type="text"
                  value={form.event_date}
                  onChange={update("event_date")}
                  className="input-base"
                  placeholder="Approx. or specific"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                  Event Type
                </label>
                <select
                  data-testid="form-event-type"
                  value={form.event_type}
                  onChange={update("event_type")}
                  className="input-base"
                >
                  <option value="">Select…</option>
                  <option>Cause for Applause Event</option>
                  <option>Corporate Keynote</option>
                  <option>Dental Conference</option>
                  <option>Association Meeting</option>
                  <option>Leadership Retreat</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                  Audience Size
                </label>
                <select
                  data-testid="form-audience-size"
                  value={form.audience_size}
                  onChange={update("audience_size")}
                  className="input-base"
                >
                  <option value="">Select…</option>
                  <option>Under 100</option>
                  <option>100 – 500</option>
                  <option>500 – 2,000</option>
                  <option>2,000+</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 block mb-2">
                Tell Gary about your event *
              </label>
              <textarea
                data-testid="form-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                className="input-base resize-none"
                placeholder="Audience, goals, what success looks like…"
                required
              />
            </div>

            <button
              type="submit"
              data-testid="form-submit"
              disabled={loading}
              className="btn-primary w-full md:w-auto disabled:opacity-50"
            >
              {loading ? "Sending…" : (
                <>
                  Send Booking Inquiry <Send size={16} />
                </>
              )}
            </button>

            {success && (
              <div
                data-testid="form-success"
                className="flex items-center gap-3 text-[#EC1985] font-bold text-sm uppercase tracking-[0.15em] pt-2"
              >
                <CheckCircle2 size={18} /> Inquiry received. Talk soon.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
